import { AfterViewInit, Component, ElementRef, forwardRef, Input, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, UntypedFormControl, ValidationErrors, Validators } from '@angular/forms';
import { NgxSignatureOptions, NgxSignaturePadComponent } from '@eve-sama/ngx-signature-pad';
import { Subscription } from 'rxjs';

/**
 * Runtime signature widget.
 *
 * Control value state machine:
 *   null                     -> no signature, pad is empty and drawable
 *   'data:image/png;base64,' -> drawn but not uploaded yet (pending commit)
 *   'https://...'            -> uploaded, read only, image view with a Clear action
 *
 * The upload itself is not done here. UserWorkflowComponent commits the pending
 * data url when the user leaves the step, so a drawing is never lost silently.
 */
@Component({
    selector: 'app-signature-widget',
    templateUrl: './signature-widget.component.html',
    styleUrls: ['./signature-widget.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SignatureWidgetComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => SignatureWidgetComponent),
            multi: true,
        },
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SignatureWidgetComponent implements ControlValueAccessor, OnInit, AfterViewInit, OnDestroy {

  @Input() item

  @ViewChild('padContainer') padContainer: ElementRef<HTMLElement>

  private padRef: NgxSignaturePadComponent = null
  private padReady = false
  private pendingSize: { width: number, height: number } = null
  private resizeObserver: ResizeObserver = null

  @ViewChild('signaturePad') set signaturePad(ref: NgxSignaturePadComponent) {
    this.padRef = ref
    this.padReady = false
    if (ref) {
      // The pad creates its canvas in its own ngAfterViewInit. Pushing a new
      // options object before that would make the library read an uninitialised
      // canvas, so the first size update is deferred by one tick.
      setTimeout(() => {
        this.padReady = true
        this.applyPendingSize()
      })
    }
  }

  signatureControl = new UntypedFormControl()

  padOptions: NgxSignatureOptions = {
    backgroundColor: '#FFF',
    penColor: '#000',
    width: 300,
    height: 150,
    css: {
      'touch-action': 'none',
      'background-color': '#fff',
      'border-radius': '10px',
      // The drawing buffer is set to the measured container size, so the css box
      // must follow the container too. signature_pad maps pointer coordinates
      // one to one, and a mismatch would offset every stroke.
      'width': '100%',
      'height': '100%'
    }
  }

  disabled = false
  touched = false

  private subscriptions: Subscription[] = []

  onChange = (value) => {}
  onTouched = () => {}

  get isCommitted(): boolean {
    const value = this.signatureControl.value
    return typeof value === 'string' && value.length > 0 && !value.startsWith('data:')
  }

  get isPending(): boolean {
    const value = this.signatureControl.value
    return typeof value === 'string' && value.startsWith('data:')
  }

  writeValue(value: any) {
    this.signatureControl.patchValue(value, { emitEvent: false })
  }

  registerOnChange(onChange: any) {
    this.onChange = onChange
  }

  registerOnTouched(onTouched: any) {
    this.onTouched = onTouched
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouched()
      this.touched = true
    }
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled
  }

  validate(): ValidationErrors | null {
    return this.signatureControl.valid ? null : this.signatureControl.errors
  }

  ngOnInit(): void {
    if (this.item?.templateOptions?.required) {
      this.signatureControl.setValidators(Validators.required)
      this.signatureControl.updateValueAndValidity({ emitEvent: false })
    }
    this.subscriptions.push(this.signatureControl.valueChanges.subscribe(value => this.onChange(value)))
  }

  ngAfterViewInit(): void {
    this.observeContainer()
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect()
    this.subscriptions.forEach(sub => sub.unsubscribe())
  }

  endSign(): void {
    if (!this.padRef || this.padRef.isEmpty()) { return }
    this.markAsTouched()
    // Default type of toDataURL is image/png.
    this.signatureControl.patchValue(this.padRef.toDataURL())
  }

  clear(): void {
    this.padRef?.clear()
    this.markAsTouched()
    // Only the reference is dropped. A file already uploaded to the account is
    // not deleted here, because deletion is destructive and out of scope.
    this.signatureControl.patchValue(null)
  }

  /**
   * The pad needs explicit pixel sizes, so the container is measured and the
   * result is pushed into options. This keeps the pad usable in row, column and
   * fullscreen step templates.
   */
  private observeContainer(): void {
    if (typeof ResizeObserver === 'undefined') { return }
    this.resizeObserver = new ResizeObserver(entries => {
      const entry = entries[0]
      if (!entry) { return }
      const width = Math.floor(entry.contentRect.width)
      const height = Math.floor(entry.contentRect.height)
      if (width < 40 || height < 40) { return }
      this.pendingSize = { width, height }
      this.applyPendingSize()
    })
    if (this.padContainer?.nativeElement) {
      this.resizeObserver.observe(this.padContainer.nativeElement)
    }
  }

  private applyPendingSize(): void {
    if (!this.pendingSize || !this.padReady || !this.padRef) { return }
    const { width, height } = this.pendingSize
    if (Math.abs(width - this.padOptions.width) < 2 && Math.abs(height - this.padOptions.height) < 2) { return }
    // A new object identity is required: the pad reacts through ngOnChanges.
    this.padOptions = { ...this.padOptions, width, height }
  }
}
